import React, { useState, useRef, useEffect } from "react";
import CustomeButton from "../components/UI/CustomButton";
import { Alert, AlertDescription } from "../components/UI/alert";
import { CheckCircle2, Lock } from "lucide-react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSendOTP, useVerifyOTP } from "../hooks/authServcies";
import showToast from "../utils/toastHelper";

const otpSchema = z.object({
  otp: z.string().length(4, "OTP must be 4 digits"),
});

interface OTPInputProps {
  phoneNumber: string;
  onVerificationSuccess: () => void;
  verificationTitle?: string;
  successMessage?: string;
  isSubmitting?: boolean;
  setIsSubmitting?: (value: boolean) => void;
}

const OTPInput: React.FC<OTPInputProps> = ({
  phoneNumber,
  onVerificationSuccess,
  successMessage = "Phone verified successfully!",
  isSubmitting: externalIsSubmitting,
  setIsSubmitting: setExternalIsSubmitting,
}) => {  const [internalIsSubmitting, setInternalIsSubmitting] = useState(false);
  const isSubmitting = externalIsSubmitting !== undefined ? externalIsSubmitting : internalIsSubmitting;
  const setIsSubmitting = setExternalIsSubmitting || setInternalIsSubmitting;

  const [timer, setTimer] = useState(120);
  const [canResend, setCanResend] = useState(false);
  const [otpValues, setOtpValues] = useState(["", "", "", ""]);
  const [error, setError] = useState("");
  const [rateLimitError, setRateLimitError] = useState("");
  const [verificationSuccess, setVerificationSuccess] = useState(false);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { verifyOtp } = useVerifyOTP();
  const { sendOtp } = useSendOTP();
  const { reset } = useForm({
    resolver: zodResolver(otpSchema),
  });


  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (timer > 0 && !canResend) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer, canResend]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newOtpValues = [...otpValues];
    newOtpValues[index] = value;
    setOtpValues(newOtpValues);

    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }

    if (value && index === 3) {
      const otp = newOtpValues.join("");
      if (otp.length === 4) {
        handleVerify(otp);
      }
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace") {
      if (!otpValues[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
        const newOtpValues = [...otpValues];
        newOtpValues[index - 1] = "";
        setOtpValues(newOtpValues);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");
    if (!/^\d{4}$/.test(pastedData)) return;

    const digits = pastedData.split("");
    setOtpValues(digits);
    handleVerify(pastedData);
  };

  const otpVerificationId = localStorage.getItem("otpVerificationId");

  const handleVerify = async (otp: string) => {
    if (!phoneNumber) {
      setError("Missing phone details");
      return;
    }
    if (!otpVerificationId) {
      setError("Missing verification id details");
      return;
    }

    try {
      setIsSubmitting(true);
      setError("");
      const result = await verifyOtp({
        phoneNumber: phoneNumber.startsWith("+")
          ? phoneNumber
          : `+${phoneNumber}`,
        otpCode: otp,
        verificationId: otpVerificationId,
      });

      if (result?.successful) {
        setVerificationSuccess(true);
        
        localStorage.setItem("verifiedPhone", phoneNumber.replace(/^\+251/, ""));
        
        setTimeout(() => {
          onVerificationSuccess();
        }, 1500);
      } else {
        const invalidOtpError = result?.errors?.find(error => 
          error.messages?.includes("Invalid OTP code.")
        );
        
        if (invalidOtpError) {
          showToast("Invalid OTP code", "error");
          setError("Invalid OTP code. Please try again.");
          
          resetOTPForm();
        } else {
          showToast("Verification failed", "error");
          setError("Verification failed. Please try again.");
        }
      }
    } catch (error) {
      console.error("Verification error:", error);
      if (error instanceof Error) {
        setError(`Verification failed: ${error.message}`);
        showToast(error.message, "error");
      } else {
        setError("Verification failed: An unknown error occurred");
        showToast("An unknown error occurred", "error");
      }
      
      resetOTPForm();
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleResendOTP = async () => {
    if (!phoneNumber) {
      setError("Phone number is missing");
      return;
    }

    try {
      setError("");
      setRateLimitError("");

      const formattedPhoneNumber = phoneNumber.startsWith("+")
        ? phoneNumber
        : phoneNumber.startsWith("251")
        ? `+${phoneNumber}`
        : `+251${phoneNumber}`;

      const response = await sendOtp(formattedPhoneNumber);
      
      if (!response) {
        throw new Error("No response received from server");
      }

      if (!response.successful) {
        throw new Error("Failed to send OTP. Server returned unsuccessful response.");
      }

      const verificationId = response?.item?.response?.verificationId;
      if (verificationId) {
        localStorage.setItem("otpVerificationId", verificationId);
      } else {
        throw new Error("Verification ID is undefined");
      }

      resetOTPForm();
      inputRefs.current[0]?.focus();

      showToast("OTP code sent successfully", "success");
    } catch (err) {
      console.error("Resend OTP error:", err);
      
      const error = err as Error & { isRateLimitError?: boolean };
      if (error?.isRateLimitError || error?.message?.includes("Too many OTP requests")) {
        const rateLimitMessage = error.message || "Too many OTP requests. Please try again later.";
        setRateLimitError(rateLimitMessage);
        showToast(rateLimitMessage, "error");
      } else {
        setError("Failed to resend OTP. Please try again.");
        showToast("Failed to resend verification code", "error");
      }
    }
  };

  const resetOTPForm = () => {
    setOtpValues(["", "", "", ""]);
    setTimer(120);
    setCanResend(false);
    reset();
    inputRefs.current[0]?.focus();
  };

  return (
    <>
      {error && !rateLimitError && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {rateLimitError && (
        <Alert className="border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 shadow-lg">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <Lock className="h-5 w-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <AlertDescription className="text-amber-900">
                <div className="font-semibold text-base mb-1">Rate Limit Exceeded</div>
                <div className="text-sm leading-relaxed">{rateLimitError}</div>
              </AlertDescription>
            </div>
          </div>
        </Alert>
      )}

      {verificationSuccess ? (
        <div className="flex flex-col items-center py-6">
          <CheckCircle2 size={64} className="text-green-500 mb-4" />
          <p className="text-center text-gray-700">
            {successMessage}
          </p>
        </div>
      ) : (
        <>
          <div className="flex justify-center gap-3">
            {otpValues.map((value, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={value}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className="w-14 h-14 text-center text-2xl font-semibold border rounded-lg focus:border-sky-500 focus:ring-2 focus:ring-sky-500 outline-none disabled:bg-gray-100"
                disabled={isSubmitting || !!rateLimitError}
                autoComplete="one-time-code"
              />
            ))}
          </div>
          <div className="text-center space-y-4">
            <div className="text-sm text-gray-600">
              {canResend && !rateLimitError ? (
                <button
                  onClick={handleResendOTP}
                  className="text-sky-600 hover:text-sky-700 font-medium disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                  disabled={isSubmitting}
                >
                  Resend Code
                </button>
              ) : rateLimitError ? (
                <span className="text-amber-600 text-sm font-medium">
                  Please wait before requesting another code
                </span>
              ) : (
                <span>
                  Resend code in{" "}
                  <span className="font-medium text-gray-900">
                    {formatTime(timer)}
                  </span>
                </span>
              )}
            </div>
            <CustomeButton
              onClick={() => handleVerify(otpValues.join(""))}
              className="w-full bg-sky-500 hover:bg-sky-600"
              title={isSubmitting ? "Verifying..." : "Verify"}
              disabled={otpValues.join("").length !== 4 || isSubmitting || !!rateLimitError}
            />
          </div>
        </>
      )}
    </>
  );
};

export default OTPInput;